import React, { useEffect, useState, Fragment, useRef } from "react";
import { Grid, Dropdown, Menu, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import {
  Input as Input,
  Button,
  Box,
  Pagination,
  Select,
  DateRangePicker,
} from "components";
import { DownloadIcon, MoreIcon, ConfirmFailIcon } from "icons";
import { IOption } from "components/select/Select";
import { localStorageHelper, generateDynamicRecords, generateCSV } from "utils";

import { ConsultationsPayload } from "models/consultations/consultationList";
import { IConsultCSVData } from "models/consultations/downloadConsultationCSV";
import {
  GetConsultationsActionCreators,
  DownloadConsultationCSVActionCreators,
} from "redux/rootActions";
import {
  selectIsLoadingConsultationList,
  selectConsultationListErrorMessage,
  consultationListDataResponse,
} from "redux/consultations/consultationList/consultationListStates";
import {
  selectIsDownloadingConsultationCSV,
  selectDownloadConsultationCSVError,
  downloadConsultationCSVDataResponse,
} from "redux/consultations/downloadConsultationCSV/downloadConsultationCSVStates";

import {
  TaskWrapperRight,
  PaginationWrapper,
  ContentWrapper,
  SearchWrapper,
  SelectWrapper,
  TableStyled,
  TaskWrapper,
  WrapperAction,
} from "./EducationConsultationList.style";
import ConsultationDetailsModal from "./ConsultationDetailsModal";

const { useBreakpoint } = Grid;
const dateFormat = "YYYY-MM-DD";

export const returnTest = (text: string) => {
  if (!text) {
    return "";
  } else if (text.toLowerCase().startsWith("test")) {
    return "";
  } else {
    return text;
  }
};

export const returnTestComment = (text: string) => {
  if (!text) {
    return "";
  } else if (text.toLowerCase().startsWith("free test")) {
    return "";
  } else {
    return text;
  }
};

const EducationConsultationList: React.FC = () => {
  const screens = useBreakpoint();
  const dispatch = useDispatch();
  const scrollAbleRef = useRef<any>(undefined);
  const currentLanguage = localStorageHelper.getItem("i18nextLng");

  const isLoadingConsultationList = useSelector(
    selectIsLoadingConsultationList
  );
  const isDownloading = useSelector(selectIsDownloadingConsultationCSV);

  const errorConsultationList = useSelector(selectConsultationListErrorMessage);
  const errorDownloadCSV = useSelector(selectDownloadConsultationCSVError);

  const consultationListData = useSelector(consultationListDataResponse);
  const downloadConsultationCSVData = useSelector(
    downloadConsultationCSVDataResponse
  );
  const [startDate, setStartDate] = useState(
    moment().subtract(1, "M").toDate()
  );
  const [endDate, setEndDate] = useState(new Date());
  const [input, setInput] = useState({
    page: 1,
    per_page: 10,
  });
  const [isVisibleConsultationDetails, setIsVisibleConsultationDetails] =
    useState(false);

  const [recordOptions, setRecordOptions] = useState<IOption[]>([
    {
      id: 1,
      name: `10件`,
      value: 10,
    },
    {
      id: 2,
      name: `50件`,
      value: 50,
    },
    {
      id: 3,
      name: `100件`,
      value: 100,
    },
    {
      id: 4,
      name: `150件`,
      value: 150,
    },
    {
      id: 5,
      name: `200件`,
      value: 200,
    },
  ]);
  const [selectedId, setSelectedId] = useState<any>(undefined);
  const totalRecord = consultationListData?.total_record || 0;

  const getConsultationsApiCall = () => {
    const paramsConsultationList: ConsultationsPayload = {
      ...input,
      from_date: moment(startDate).format(dateFormat),
      to_date: moment(endDate).format(dateFormat),
    };
    dispatch(
      GetConsultationsActionCreators.getConsultationListAction(
        paramsConsultationList
      )
    );
  };

  useEffect(() => {
    dispatch(GetConsultationsActionCreators.handleResetDataAction());
    dispatch(DownloadConsultationCSVActionCreators.handleResetDataAction());
  }, []);

  useEffect(() => {
    if (errorConsultationList) {
      // notification.error({
      //   placement: "bottomRight",
      //   message: "Error getting consultations list",
      //   icon: <ConfirmFailIcon width={53} height={53} />,
      // });
      dispatch(GetConsultationsActionCreators.handleResetDataAction());
    }
  }, [errorConsultationList]);

  useEffect(() => {
    getConsultationsApiCall();
  }, [input.page, input.per_page]);

  const genderGroup: any = {
    0: "男性",
    1: "女性",
    2: "回答なし",
  };

  const returnDiagnosisTitle = (title: string) => {
    if (title && title.toLowerCase() === "comment") {
      return "医師がフリーコメントで回答";
    } else {
      if (
        title?.toLowerCase().startsWith("comment,") ||
        title?.toLowerCase().startsWith("comment ,")
      ) {
        return title.split(",")[1].trim();
      } else if (
        title?.toLowerCase().endsWith(",comment") ||
        title?.toLowerCase().endsWith(", comment")
      ) {
        return title.split(",")[0].trim();
      }
      return title;
    }
  };

  const convertFileData = (data: IConsultCSVData[]) => {
    const convertedData: any[] = [];
    if (data.length && data.length > 0) {
      data.map((item: IConsultCSVData, index: number) => {
        convertedData.push({
          id: index + 1,
          patient_age: item?.patient?.age,
          patient_gender: genderGroup[item?.patient?.gender],
          disease_name: item.disease_name ? item.disease_name : "",
          consult_date: item.consultation_created_at
            ? moment(item.consultation_created_at).format(dateFormat)
            : "",
          consult_time: item.consultation_created_at
            ? moment.parseZone(item.consultation_created_at).format("HH時mm分")
            : "",
          answer_date: item?.is_cancel
            ? "キャンセル"
            : item.doctor_response_created_at
            ? moment(item.doctor_response_created_at).format(dateFormat)
            : "",
          suspected_disease_name: item.doctor_response.diagnosis_title
            ? `${returnDiagnosisTitle(item.doctor_response.diagnosis_title)}\n`
            : item.doctor_response.diagnosis_comment
            ? "医師がフリーコメントで回答"
            : "",
          ans_1: item?.doctor_response?.hospital_names
            ? item.doctor_response.hospital_names.replaceAll("ギ", "ギ")
            : "",
          ans_2: item.doctor_response.medical_instruction_title
            ? item.doctor_response.medical_instruction_title
            : item?.doctor_response?.medical_instruction_comment
            ? "医師がフリーコメントで回答"
            : "",
          ans_3: item.doctor_response.medicines_name
            ? item.doctor_response.medicines_name
            : item?.doctor_response?.medicine_comment
            ? "医師がフリーコメントで回答"
            : "",
        });
      });
    }
    return convertedData;
  };

  const convertFileData2 = (data: IConsultCSVData[]) => {
    const convertedData: any[] = [];
    if (data.length && data.length > 0) {
      data.map((item: IConsultCSVData, index: number) => {
        convertedData.push({
          id: index + 1,
          patient_age: item?.patient?.age,
          patient_gender: genderGroup[item?.patient?.gender],
          disease_name: item.disease_name ? item.disease_name : "",
          consult_answer_date: `相：${
            item.consultation_created_at
              ? moment(item.consultation_created_at).format(dateFormat)
              : "-"
          }\n回：${
            item.doctor_response_created_at
              ? moment(item.doctor_response_created_at).format(dateFormat)
              : "-"
          }`,
          suspected_disease_name: item.doctor_response.diagnosis_title
            ? `${returnDiagnosisTitle(item.doctor_response.diagnosis_title)}\n`
            : item.doctor_response.diagnosis_comment
            ? "医師がフリーコメントで回答\n"
            : "",
          answer_details: `${
            item?.doctor_response?.hospital_names
              ? `おすすめの診療科：${item.doctor_response.hospital_names.replaceAll(
                  "ギ",
                  "ギ"
                )}\n`
              : ""
          }${
            item.doctor_response.medical_instruction_title
              ? `緊急性：${item.doctor_response.medical_instruction_title}\n`
              : item?.doctor_response?.medical_instruction_comment
              ? "緊急性：医師がフリーコメントで回答\n"
              : ""
          }${
            item.doctor_response.medicines_name
              ? `おすすめの市販薬：${item.doctor_response.medicines_name}\n`
              : item?.doctor_response?.medicine_comment
              ? "おすすめの市販薬：医師がフリーコメントで回答\n"
              : ""
          }`,
          // doctor_name: item.doctor_name ? item.doctor_name : "",
        });
      });
    }
    return convertedData;
  };

  useEffect(() => {
    if (downloadConsultationCSVData && !isDownloading) {
      const fileData: IConsultCSVData[] =
        downloadConsultationCSVData?.result &&
        downloadConsultationCSVData.result.length &&
        downloadConsultationCSVData.result.length > 0
          ? downloadConsultationCSVData.result
          : [];
      const downloadData = convertFileData(fileData);
      const currentDate = moment(new Date()).format("YYYYMMDD");
      const fileName = `supercity_consultations_${currentDate}.csv`;
      const headers = [
        { label: "ID", key: "id" },
        { label: "年齢", key: "patient_age" },
        { label: "性別", key: "patient_gender" },
        { label: "主訴", key: "disease_name" },
        {
          label: "相談日",
          key: "consult_date",
        },
        {
          label: "相談時間",
          key: "consult_time",
        },
        {
          label: "回答日",
          key: "answer_date",
        },
        { label: "疑い病名", key: "suspected_disease_name" },
        { label: "おすすめの診療科", key: "ans_1" },
        { label: "緊急性", key: "ans_2" },
        { label: "おすすめの市販薬", key: "ans_3" },
        // { label: "回答内容", key: "answer_details" },
        // { label: "回答した医師", key: "doctor_name" },
      ];
      generateCSV(fileName, headers, downloadData);
      dispatch(DownloadConsultationCSVActionCreators.handleResetDataAction());
    }
  }, [downloadConsultationCSVData]);

  useEffect(() => {
    if (errorDownloadCSV) {
      dispatch(DownloadConsultationCSVActionCreators.handleResetDataAction());
    }
  }, [errorDownloadCSV]);

  const menu = (
    <Menu>
      <Menu.Item>
        <Button
          icon={
            <DownloadIcon
              width="14px"
              height="14px"
              fill="currentColor"
              style={{ position: "absolute", left: 4, top: 6 }}
            />
          }
          name="CSV出力"
          background="#2AC769"
          color="#FFFFFF"
          border="none"
          fontSize={12}
          fontWeight={700}
          lineHeight="18px"
          padding="3px 7px 3px 16px"
          onClick={() => {
            dispatch(
              DownloadConsultationCSVActionCreators.downloadConsultationCSVAction(
                {
                  from_date: moment(startDate, dateFormat).format(dateFormat),
                  to_date: moment(endDate, dateFormat).format(dateFormat),
                }
              )
            );
          }}
          disabled={isDownloading}
          loading={isDownloading}
          margin="0 8px 0"
        />
        {/* <Button
                    icon={
                        <DownloadIcon
                            width="14px"
                            height="14px"
                            fill="currentColor"
                            style={{ position: "absolute", left: 4, top: 6 }}
                        />
                    }
                    name={t("pdf-output")}
                    background="#2AC769"
                    color="#FFFFFF"
                    border="none"
                    fontSize={12}
                    fontWeight={700}
                    padding="3px 7px 3px 16px"
                    onClick={() => {
                        //
                    }}
                /> */}
      </Menu.Item>
    </Menu>
  );

  const returnStatus = (status: number) => {
    switch (status) {
      case 0:
        return "キャンセル";
      case 1:
        return "相談中";
      case 2:
        return "相談済";
    }
  };

  const columns = [
    {
      title: "主訴",
      dataIndex: "disease_name",
      key: "disease_name",
      width: 144,
      // fixed: window.innerWidth < 576 ? "" : "left",
    },
    {
      title: (
        <div>
          <div>相談日 </div>
          <div>回答日</div>
        </div>
      ),
      dataIndex: "consult_answer_date",
      key: "consult_answer_date",
      width: 130,
      render: (text: string, record: any) => (
        <>
          <div>
            相：
            {record?.consultation_created_at
              ? moment(record.consultation_created_at)
                  .utc()
                  .add(9, "hours")
                  .format("YYYY/MM/DD")
              : "-"}{" "}
          </div>
          <div>
            回：
            {record?.is_cancel
              ? "キャンセル"
              : record?.doctor_response_created_at
              ? moment(record.doctor_response_created_at)
                  .utc()
                  .add(9, "hours")
                  .format("YYYY/MM/DD")
              : "-"}
          </div>
        </>
      ),
    },
    {
      title: "疑い病名",
      dataIndex: "suspected_disease_name",
      key: "suspected_disease_name",
      width: 153,
      render: (text: string, record: any) => {
        if (
          !record?.doctor_response ||
          (record?.doctor_response &&
            Object.keys(record.doctor_response).length &&
            !record.doctor_response.diagnosis_title)
        ) {
          return (
            <div>
              {record?.doctor_response?.diagnosis_comment
                ? "医師がフリーコメントで回答"
                : ""}
            </div>
          );
        }
        return (
          <div>
            <div>
              {returnDiagnosisTitle(record.doctor_response.diagnosis_title)}
            </div>
          </div>
        );
      },
    },
    {
      title: "回答内容",
      dataIndex: "answer_details",
      key: "answer_details",
      width: 487,
      render: (text: string, record: any) => (
        <div style={{ fontWeight: 700 }}>
          {record?.doctor_response?.hospital_names && (
            <div>
              おすすめの診療科：
              <span style={{ fontWeight: 500 }}>
                {record?.doctor_response?.hospital_names}
              </span>
            </div>
          )}
          {record?.doctor_response?.medical_instruction_title ? (
            <div>
              緊急性：
              <span style={{ fontWeight: 500 }}>
                {record?.doctor_response?.medical_instruction_title}
              </span>
            </div>
          ) : record?.doctor_response?.medical_instruction_comment ? (
            <div>
              緊急性：
              <span style={{ fontWeight: 500 }}>
                医師がフリーコメントで回答
              </span>
            </div>
          ) : (
            <></>
          )}
          {record?.doctor_response?.medicines_name ? (
            <div>
              おすすめの市販薬：
              <span style={{ fontWeight: 500 }}>
                {record?.doctor_response?.medicines_name}
              </span>
            </div>
          ) : record?.doctor_response?.medicine_comment ? (
            <div>
              おすすめの市販薬：
              <span style={{ fontWeight: 500 }}>
                医師がフリーコメントで回答
              </span>
            </div>
          ) : (
            <></>
          )}
          {/* {record?.doctor_response?.comment &&
            returnTestComment(record.doctor_response.comment) ? (
            <div>
              コメント：
              <span style={{ fontWeight: 500 }}>
                {record?.doctor_response?.comment}
              </span>
            </div>
          ) : (
            <></>
          )} */}
          {/* {record?.doctor_response?.diagnosis_comment && (
            <div style={{ fontWeight: 500 }}>
              {record?.doctor_response?.diagnosis_comment}
            </div>
          )} */}
          {/* {record?.doctor_response?.start_greeting && (
            <div style={{ fontWeight: 500 }}>
              {record?.doctor_response?.start_greeting}
            </div>
          )}
          {record?.doctor_response?.end_greeting && (
            <div style={{ fontWeight: 500 }}>
              {record?.doctor_response?.end_greeting}
            </div>
          )} */}
        </div>
      ),
    },
    // {
    //   title: "回答した医師",
    //   dataIndex: "doctor_name",
    //   key: "doctor_name",
    //   width: 142,
    //   render: (text: string, record: any) => {
    //     // if (!text || (text && text === "")) {
    //     //   return <div>-</div>;
    //     // }
    //     return <div>{text}</div>;
    //   },
    // },
    {
      title: "",
      dataIndex: "",
      key: "8",
      width: 88,
      render: (text: any, record: any) => (
        <Button
          name="詳細"
          background="#2AC769"
          color="#FFFFFF"
          border="none"
          fontSize={16}
          fontWeight={700}
          padding="3px 16px"
          onClick={() => {
            setSelectedId(record?.id);
            setIsVisibleConsultationDetails(true);
          }}
          width="max-content"
          disabled={isLoadingConsultationList}
        />
      ),
    },
  ];

  const handleDate = (date: string) => {
    const currentTimeZone = localStorageHelper.getItem("currentTimeZone");
    const format =
      currentLanguage == "ja" ? "YYYY年MM月DD日 HH:mm" : "YYYY-MM-DD HH:mm";

    return moment(date).utc().add(currentTimeZone, "hours").format(format);
  };

  // useEffect(() => {
  //   if (errorMessageUserList) {
  //     notification.error({
  //       placement: "bottomRight",
  //       message: errorMessageUserList,
  //     });
  //     dispatch(UserListActionCreators.handleResetDataAction());
  //   }
  // }, [errorMessageUserList]);

  const handleChangePage = (page: number) => {
    setInput((prevState: any) => ({ ...prevState, page }));
    if (
      scrollAbleRef.current &&
      window.scrollY >= scrollAbleRef.current.offsetTop
    ) {
      window.scrollTo(0, 0);
    }
  };

  const handleOnChangeSizeChange = (sizeChange: number) => {
    setInput((prevState: any) => ({
      ...prevState,
      per_page: sizeChange,
      page: 1,
    }));
  };

  const ActionSection = () => {
    return (
      <TaskWrapper>
        <TaskWrapperRight>
          <Button
            icon={
              <DownloadIcon
                width="14px"
                height="14px"
                fill="currentColor"
                style={{ position: "absolute", left: 4, top: 6 }}
              />
            }
            name="CSV出力"
            background="#2AC769"
            color="#FFFFFF"
            border="none"
            fontSize={12}
            lineHeight="18px"
            fontWeight={700}
            padding="3px 7px 3px 16px"
            style={{
              minWidth: 75,
              height: 24,
              padding: isDownloading ? "3px 7px" : "3px 7px 3px 16px",
            }}
            onClick={() => {
              dispatch(
                DownloadConsultationCSVActionCreators.downloadConsultationCSVAction(
                  {
                    from_date: moment(startDate, dateFormat).format(dateFormat),
                    to_date: moment(endDate, dateFormat).format(dateFormat),
                  }
                )
              );
            }}
            disabled={isDownloading}
            loading={isDownloading}
            size={17}
          />
          <Dropdown overlay={menu} placement="bottomRight" arrow>
            <MoreIcon fill="#B7B7B7" />
          </Dropdown>
        </TaskWrapperRight>
      </TaskWrapper>
    );
  };

  const ShowPagination = () => {
    return totalRecord > input.per_page ? (
      <PaginationWrapper>
        <Pagination
          current={input.page}
          onChange={handleChangePage}
          pageSize={input.per_page}
          total={totalRecord}
          showSizeChanger={false}
          showLessItems={!screens.xl}
        />
      </PaginationWrapper>
    ) : (
      <Fragment></Fragment>
    );
  };

  return (
    <Box
      title="相談一覧"
      subTitle={`合計相談数：
        ${totalRecord}`}
      padding="24px 16px"
      action={
        <SelectWrapper>
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              marginRight: 5,
            }}
          >
            表示件数：
          </div>
          <Select
            defaultValue={10}
            options={recordOptions}
            onChange={handleOnChangeSizeChange}
          />
        </SelectWrapper>
      }
    >
      <SearchWrapper>
        <WrapperAction>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            locale={"ja"}
          />
          <Button
            name="検索"
            background="#2AC769"
            color="#FFFFFF"
            border="none"
            fontSize={16}
            fontWeight={700}
            padding="2px 16px 0px"
            onClick={() => {
              setInput((prevState: any) => ({ ...prevState, page: 1 }));
              if (input.page === 1) {
                getConsultationsApiCall();
              }
            }}
            disabled={isLoadingConsultationList}
          />
        </WrapperAction>

        <ShowPagination />
      </SearchWrapper>

      <ContentWrapper
        style={{ marginBottom: totalRecord > input.per_page ? 24 : 0 }}
        ref={scrollAbleRef}
      >
        <TableStyled
          dataSource={
            consultationListData?.result ? consultationListData.result : []
          }
          // dataSource={tempData}
          columns={columns}
          actionSection={<ActionSection />}
          scroll={!consultationListData?.result ? {} : { x: "max-content" }}
          loading={isLoadingConsultationList}
        />
      </ContentWrapper>
      <ShowPagination />

      <ConsultationDetailsModal
        type="consultation"
        isVisibleModal={isVisibleConsultationDetails}
        setIsVisibleModal={setIsVisibleConsultationDetails}
        id={selectedId}
      />
    </Box>
  );
};

export default EducationConsultationList;
